import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatGBPCompact, formatApiError, amountToPos, posToAmount, SLIDER_STEPS } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Sparkles, Check, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const PURPOSES = ["Working Capital", "Equipment / Asset Finance", "Commercial Mortgage", "Invoice Finance", "Growth Capital", "Bridging Finance", "Business Expansion", "Other"];
const SECTORS = ["Retail", "Hospitality", "Construction", "Manufacturing", "Professional Services", "Healthcare", "Technology", "Transport & Logistics", "Property", "Wholesale", "Agriculture", "Other"];

export function PreQualifierModal({ open, onOpenChange }) {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    amount: 250_000, term_months: 36, purpose: "Working Capital",
    sector: "Professional Services", annual_turnover: 0,
  });

  useEffect(() => {
    if (open) { setStep(1); setResult(null); setForm((f) => ({ ...f })); }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const check = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/prequalify", form);
      setResult(data);
      setStep(3);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const goApply = () => {
    onOpenChange(false);
    const p = new URLSearchParams({ amount: String(form.amount), term: String(form.term_months) });
    nav(`/apply?${p.toString()}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white border-line rounded-[24px] p-0 overflow-hidden" data-testid="prequalifier-modal">
        <DialogHeader className="bg-navy text-white p-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold w-fit mb-3">
            <Sparkles className="h-3 w-3" strokeWidth={2.6} /> 60-second eligibility check
          </div>
          <DialogTitle className="font-display font-extrabold text-[28px] tracking-[-0.03em] leading-tight text-white">
            {step === 3 ? "Here's what fits" : "How much does your business need?"}
          </DialogTitle>
          <p className="text-white/65 text-[13.5px] font-medium">
            {step === 1 && "Step 1 of 2 — quick amount, term and purpose."}
            {step === 2 && "Step 2 of 2 — sector and turnover so we can shortlist products."}
            {step === 3 && "Recommended products for your profile. No footprint on the business file."}
          </p>
        </DialogHeader>
        <div className="p-7 max-h-[70vh] overflow-y-auto">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70">Amount</Label>
                  <div className="font-display font-extrabold text-[26px] tracking-[-0.02em] text-ink" data-testid="preq-amount">{formatGBPCompact(form.amount)}</div>
                </div>
                <Slider value={[amountToPos(form.amount)]} min={0} max={SLIDER_STEPS} step={1}
                  onValueChange={(v) => set("amount", posToAmount(v[0]))} data-testid="preq-amount-slider" />
                <div className="flex justify-between font-mono text-[10px] text-ink/40 mt-2 font-semibold">
                  <span>£5k</span><span>£500k</span><span>£10M</span><span>£500M</span>
                </div>
              </div>
              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70">Term</Label>
                  <div className="font-display font-extrabold text-[18px] text-ink">{form.term_months} months</div>
                </div>
                <Slider value={[form.term_months]} min={1} max={120} step={1}
                  onValueChange={(v) => set("term_months", v[0])} data-testid="preq-term-slider" />
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-3 block">Purpose</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PURPOSES.map((p) => (
                    <button key={p} onClick={() => set("purpose", p)}
                      data-testid={`preq-purpose-${p.replace(/\s|\//g, "-").toLowerCase()}`}
                      className={`px-4 py-3 rounded-xl border text-[13px] font-bold text-left transition-all
                        ${form.purpose === p ? "bg-ink text-white border-ink" : "bg-white border-line text-ink hover:border-ink/40 hover:bg-paper-warm"}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => setStep(2)} data-testid="preq-next-1"
                className="w-full h-12 rounded-xl bg-navy text-white font-extrabold hover:bg-navy-2 inline-flex items-center justify-center gap-2 text-[14px]">
                Continue <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </button>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-3 block">Sector</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SECTORS.map((s) => (
                    <button key={s} onClick={() => set("sector", s)}
                      data-testid={`preq-sector-${s.replace(/\s|&/g, "-").toLowerCase()}`}
                      className={`px-3 py-2.5 rounded-xl border text-[13px] font-bold text-left transition-all
                        ${form.sector === s ? "bg-ink text-white border-ink" : "bg-white border-line text-ink hover:border-ink/40 hover:bg-paper-warm"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-2 block">Annual turnover (approx)</Label>
                <Input type="number" min="0" value={form.annual_turnover || ""} onChange={(e) => set("annual_turnover", Number(e.target.value))}
                  placeholder="£" data-testid="preq-turnover" className="h-12 rounded-xl font-medium" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 h-12 rounded-xl border border-line text-ink/80 font-bold hover:bg-paper-warm">Back</button>
                <button onClick={check} disabled={loading} data-testid="preq-check"
                  className="flex-[2] h-12 rounded-xl bg-emerald text-white font-extrabold hover:bg-emerald-2 inline-flex items-center justify-center gap-2 text-[14px] disabled:opacity-50 shadow-emerald">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Check eligibility <ArrowRight className="h-4 w-4" strokeWidth={2.4} /></>}
                </button>
              </div>
            </div>
          )}
          {step === 3 && result && (
            <div className="space-y-4">
              {result.products.length === 0 && (
                <div className="rounded-xl border border-coral/30 bg-coral/5 p-5 text-center">
                  <p className="text-ink/80 font-semibold">We couldn&apos;t shortlist a product from the panel automatically. Submit the full application and our team will structure a bespoke option.</p>
                </div>
              )}
              {result.products.map((p, i) => (
                <div key={p.id} data-testid={`preq-product-${p.id}`}
                  className={`rounded-2xl p-5 border-2 ${i === 0 ? "border-emerald bg-emerald-soft/60" : "border-line bg-white"}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.14em] font-extrabold text-emerald-3 mb-1">
                        {i === 0 ? "Best fit" : "Also considered"}
                      </div>
                      <div className="font-display font-extrabold text-[20px] text-navy tracking-[-0.02em]">{p.name}</div>
                    </div>
                    <div className="font-mono text-[13px] font-extrabold text-emerald-3">from {p.apr_from}% APR</div>
                  </div>
                  <p className="text-[13.5px] text-navy/70 font-medium leading-[1.55]">{p.body}</p>
                </div>
              ))}
              <button onClick={goApply} data-testid="preq-apply-btn"
                className="w-full h-12 rounded-xl bg-navy text-white font-extrabold hover:bg-navy-2 inline-flex items-center justify-center gap-2 text-[14px]">
                Continue to full application <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </button>
              <button onClick={() => onOpenChange(false)} className="w-full h-11 rounded-xl border border-line text-ink/70 font-bold hover:bg-paper-warm text-[13.5px]">
                Not now
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PreQualifierTile({ onOpen }) {
  return (
    <div className="mt-10 rounded-2xl border border-emerald/25 bg-emerald-soft/50 p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
      <div className="h-12 w-12 rounded-xl bg-emerald text-white flex items-center justify-center shrink-0">
        <Sparkles className="h-6 w-6" strokeWidth={2.2} />
      </div>
      <div className="flex-1">
        <div className="font-display font-extrabold text-[17px] md:text-[19px] tracking-[-0.02em] text-navy leading-tight mb-1">
          60-second eligibility check
        </div>
        <div className="text-[13.5px] text-navy/70 font-medium leading-[1.5]">
          Answer 4 questions and see which products fit your business — no personal details, no footprint.
        </div>
      </div>
      <button onClick={onOpen} data-testid="preq-tile-btn"
        className="btn-shine group inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-navy text-white font-extrabold text-[13.5px] hover:bg-navy-2 shrink-0">
        Check now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
      </button>
    </div>
  );
}

// Lightweight controller for the modal — usable anywhere
export function usePreQualifier() {
  const [open, setOpen] = useState(false);
  return { open, setOpen, PreQualifier: (props) => <PreQualifierModal open={open} onOpenChange={setOpen} {...props} /> };
}
